import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { BookOpen } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useMasterMenu } from '../hooks/use-menus'
import { WeekView } from '../components/week-view'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function MenusListPage() {
  const { t: tMenus } = useTranslation('menus')
  const navigate = useNavigate()
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<
    number | null
  >(null)

  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items || []
  const { data: masterData } = useMasterMenu(selectedRestaurantId)

  const openMaster = () => {
    const id = masterData?.menu.id
    if (id) navigate({ to: '/menus/$menuId', params: { menuId: String(id) } })
  }

  return (
    <PermissionGate action={ACTIONS.MENU_VIEW}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tMenus('title')}</h1>
            <p className="text-muted-foreground mt-1">{tMenus('subtitle')}</p>
          </div>
          {selectedRestaurantId !== null && (
            <Button
              variant="outline"
              onClick={openMaster}
              disabled={!masterData}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              {tMenus('masterMenu')}
            </Button>
          )}
        </div>

        <div className="max-w-md">
          <Label htmlFor="restaurant-select">
            {tMenus('filters.restaurant')}
          </Label>
          <Select
            value={selectedRestaurantId?.toString() ?? ''}
            onValueChange={(value) => setSelectedRestaurantId(parseInt(value))}
          >
            <SelectTrigger id="restaurant-select" className="mt-1.5">
              <SelectValue placeholder={tMenus('filters.selectRestaurant')} />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((restaurant) => (
                <SelectItem
                  key={restaurant.id}
                  value={restaurant.id.toString()}
                >
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedRestaurantId === null ? (
          <div className="flex items-center justify-center h-48 rounded-lg border border-dashed">
            <p className="text-sm text-muted-foreground">
              {tMenus('selectRestaurantHint')}
            </p>
          </div>
        ) : (
          <WeekView restaurantId={selectedRestaurantId} />
        )}
      </div>
    </PermissionGate>
  )
}
